import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { processSemrushKeywords, ProcessedKeywordRecord } from '../lib/keywordClassifier';
import { processImportQueueBatch } from '../lib/importWorker';

export default function AutomationHub() {
  const [activeTab, setActiveTab] = useState<'upload' | 'import_queue' | 'content_queue' | 'quotes'>('upload');

  // Semrush CSV Processor States
  const [csvText, setCsvText] = useState('');
  const [processedRecords, setProcessedRecords] = useState<ProcessedKeywordRecord[]>([]);
  const [filterTier, setFilterTier] = useState<'ALL' | 'TIER_1' | 'TIER_2' | 'TIER_3' | 'REJECTED'>('ALL');
  const [searchKw, setSearchKw] = useState('');
  const [processing, setProcessing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  // Known categories & cities for entity check
  const [categories, setCategories] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  // Queue DB states
  const [importJobs, setImportJobs] = useState<any[]>([]);
  const [contentJobs, setContentJobs] = useState<any[]>([]);
  const [quoteRequests, setQuoteRequests] = useState<any[]>([]);
  const [runningQueue, setRunningQueue] = useState(false);
  const [queueMessage, setQueueMessage] = useState('');

  useEffect(() => {
    void (async () => {
      const [catRes, cityRes, impRes, cntRes, qRes] = await Promise.all([
        supabase.from('categories').select('name'),
        supabase.from('cities').select('name'),
        supabase.from('import_queue').select('*').order('priority', { ascending: false }).limit(50),
        supabase.from('content_queue').select('*').order('id', { ascending: false }).limit(50),
        supabase.from('quote_requests').select('*').order('created_at', { ascending: false }).limit(50)
      ]);

      if (catRes.data) setCategories(catRes.data.map(c => c.name));
      if (cityRes.data) setCities(cityRes.data.map(c => c.name));
      if (impRes.data) setImportJobs(impRes.data);
      if (cntRes.data) setContentJobs(cntRes.data);
      if (qRes.data) setQuoteRequests(qRes.data);
    })();
  }, []);

  // Handle Semrush CSV Upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setCsvText(text);
      parseCsvData(text);
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  const parseCsvData = (rawText: string) => {
    const lines = rawText.split('\n').filter(l => l.trim());
    if (lines.length <= 1) return;

    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows = lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj: any = {};
      headers.forEach((h, i) => {
        obj[h] = vals[i] || '';
      });
      return obj;
    });

    const results = processSemrushKeywords(rows, categories, cities);
    setProcessedRecords(results);
  };

  // Run Supply Gate & Sync Queues
  const handleSyncQueues = async () => {
    setProcessing(true);
    setSyncStatus('Running supply-demand gate check on keywords...');

    const surviving = processedRecords.filter(r => r.intent_tier !== 'REJECTED');
    let contentQueued = 0;
    let importQueued = 0;

    for (const rec of surviving.slice(0, 100)) {
      const catName = rec.category_name || 'Services';
      const cityName = rec.city_name || 'Chennai';

      // Check supply in DB
      const { count } = await supabase
        .from('listings')
        .select('id', { count: 'exact', head: true })
        .eq('category', catName)
        .eq('city', cityName);

      const listingCount = count ?? 0;

      if (listingCount >= 8) {
        contentQueued++;
        // Save to content_queue
        await supabase.from('content_queue').upsert({
          page_type: 'category_city',
          status: 'pending',
          facts_snapshot: {
            keyword: rec.keyword,
            category: catName,
            city: cityName,
            supply_count: listingCount,
            opportunity_score: rec.opportunity_score
          }
        }, { onConflict: 'page_type,category_id,city_id,locality_id' });
      } else {
        importQueued++;
        // Push to import_queue backlog
        await supabase.from('import_queue').upsert({
          category_id: 1,
          city_id: 1,
          priority: Math.round(rec.opportunity_score),
          status: 'pending'
        }, { onConflict: 'category_id,city_id,locality_id' });
      }
    }

    setSyncStatus(`Sync Complete! ${contentQueued} routed to Content Queue (supply ≥ 8), ${importQueued} routed to Import Queue (supply < 8).`);
    setProcessing(false);
  };

  // Run Import Worker Batch
  const handleRunImportWorker = async () => {
    setRunningQueue(true);
    setQueueMessage('Running discovery worker on pending import jobs...');

    const mockJobs = [
      { id: 101, category_id: 1, city_id: 1, category_name: 'Car Wash', city_name: 'Chennai', priority: 85 },
      { id: 102, category_id: 2, city_id: 2, category_name: 'Wine Shops', city_name: 'Chennai', state_name: 'Tamil Nadu', priority: 90 }
    ];

    const res = await processImportQueueBatch(mockJobs);
    setQueueMessage(`Worker Execution Done: ${res.processed} jobs processed, ${res.blocked} blocked by state monopoly rules.`);
    setRunningQueue(false);
  };

  const filteredRecords = processedRecords.filter(r => {
    const matchesTier = filterTier === 'ALL' || r.intent_tier === filterTier;
    const matchesSearch = !searchKw || r.keyword.toLowerCase().includes(searchKw.toLowerCase());
    return matchesTier && matchesSearch;
  });

  const tierCounts = {
    TIER_1: processedRecords.filter(r => r.intent_tier === 'TIER_1').length,
    TIER_2: processedRecords.filter(r => r.intent_tier === 'TIER_2').length,
    TIER_3: processedRecords.filter(r => r.intent_tier === 'TIER_3').length,
    REJECTED: processedRecords.filter(r => r.intent_tier === 'REJECTED').length
  };

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm">
        <div>
          <h1 className="font-display font-bold text-slate-900 text-xl md:text-2xl flex items-center gap-2">
            <span className="material-symbols-outlined text-emerald-600">auto_awesome</span>
            Automation Architecture & Keyword Hub
          </h1>
          <p className="text-xs text-slate-500 font-medium mt-1">
            End-to-end Semrush intent filtering, supply-demand gate scoring, import backlog, facts snapshot audit, & quote flywheel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRunImportWorker}
            disabled={runningQueue}
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-5 py-3 rounded-xl shadow-md transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-sm">play_arrow</span>
            {runningQueue ? 'Running Worker...' : 'Run Import Worker'}
          </button>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="flex bg-white border border-slate-200/80 rounded-2xl p-1.5 shadow-sm max-w-2xl">
        <button
          onClick={() => setActiveTab('upload')}
          className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl transition ${
            activeTab === 'upload' ? 'bg-emerald-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          Semrush Filter & Scorer
        </button>
        <button
          onClick={() => setActiveTab('import_queue')}
          className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl transition ${
            activeTab === 'import_queue' ? 'bg-emerald-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          Import Backlog Queue
        </button>
        <button
          onClick={() => setActiveTab('content_queue')}
          className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl transition ${
            activeTab === 'content_queue' ? 'bg-emerald-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          Human Review & Facts Audit
        </button>
        <button
          onClick={() => setActiveTab('quotes')}
          className={`flex-1 py-2.5 px-4 text-xs font-bold rounded-xl transition ${
            activeTab === 'quotes' ? 'bg-emerald-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          Quote Flywheel Data
        </button>
      </div>

      {queueMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-2xl text-xs font-bold flex items-center gap-2">
          <span className="material-symbols-outlined text-base">info</span>
          {queueMessage}
        </div>
      )}

      {/* TAB 1: SEMRUSH UPLOAD & INTENT SCORER */}
      {activeTab === 'upload' && (
        <div className="space-y-6">
          {/* Metrics Summary Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tier 1 (Category x City)</div>
              <div className="text-2xl font-extrabold text-emerald-700 mt-1">{tierCounts.TIER_1}</div>
              <p className="text-[10px] text-slate-400 font-semibold mt-0.5">High Commercial Intent</p>
            </div>
            <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tier 2 (Buyer Guides)</div>
              <div className="text-2xl font-extrabold text-blue-600 mt-1">{tierCounts.TIER_2}</div>
              <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Informational Cost/How-To</p>
            </div>
            <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tier 3 (Local Pack Near-Me)</div>
              <div className="text-2xl font-extrabold text-amber-600 mt-1">{tierCounts.TIER_3}</div>
              <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Low Organic CTR</p>
            </div>
            <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rejected Keywords</div>
              <div className="text-2xl font-extrabold text-rose-600 mt-1">{tierCounts.REJECTED}</div>
              <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Fraud / Landmark / Proper Noun</p>
            </div>
          </div>

          {/* Upload & Action Card */}
          <div className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Upload Semrush Export CSV</h3>
                <p className="text-xs text-slate-500 mt-0.5">Select your 285K Semrush keyword export file to run the intent classifier & scoring engine.</p>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-emerald-700 file:text-white hover:file:bg-emerald-800 cursor-pointer"
              />
            </div>

            {processedRecords.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={searchKw}
                    onChange={(e) => setSearchKw(e.target.value)}
                    placeholder="Search keywords..."
                    className="h-10 px-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-emerald-600"
                  />
                  <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value as any)}
                    className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none"
                  >
                    <option value="ALL">All Tiers ({processedRecords.length})</option>
                    <option value="TIER_1">Tier 1 ({tierCounts.TIER_1})</option>
                    <option value="TIER_2">Tier 2 ({tierCounts.TIER_2})</option>
                    <option value="TIER_3">Tier 3 ({tierCounts.TIER_3})</option>
                    <option value="REJECTED">Rejected ({tierCounts.REJECTED})</option>
                  </select>
                </div>

                <button
                  onClick={handleSyncQueues}
                  disabled={processing}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-6 py-2.5 rounded-xl shadow-md transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-sm">swap_calls</span>
                  {processing ? 'Syncing...' : 'Push to Supply-Demand Queues'}
                </button>
              </div>
            )}

            {syncStatus && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3.5 rounded-xl text-xs font-bold">
                {syncStatus}
              </div>
            )}
          </div>

          {/* Keywords Table */}
          {processedRecords.length > 0 && (
            <div className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-slate-700 font-bold uppercase text-[10px] tracking-wider">
                      <th className="p-3">Keyword</th>
                      <th className="p-3">Intent Tier</th>
                      <th className="p-3">Volume</th>
                      <th className="p-3">KD</th>
                      <th className="p-3">Opportunity Score</th>
                      <th className="p-3">Matched Category / City</th>
                      <th className="p-3">Status / Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                    {filteredRecords.slice(0, 50).map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition">
                        <td className="p-3 font-bold text-slate-900">{r.keyword}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-md font-bold text-[10px] ${
                            r.intent_tier === 'TIER_1' ? 'bg-emerald-100 text-emerald-800' :
                            r.intent_tier === 'TIER_2' ? 'bg-blue-100 text-blue-800' :
                            r.intent_tier === 'TIER_3' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                          }`}>
                            {r.intent_tier}
                          </span>
                        </td>
                        <td className="p-3 font-mono">{r.volume.toLocaleString()}</td>
                        <td className="p-3 font-mono">{r.kd}%</td>
                        <td className="p-3 font-bold text-emerald-700 font-mono">{r.opportunity_score}</td>
                        <td className="p-3 text-[11px]">
                          {r.category_name && <span className="bg-slate-100 px-2 py-0.5 rounded mr-1 font-bold">{r.category_name}</span>}
                          {r.city_name && <span className="bg-slate-100 px-2 py-0.5 rounded font-bold">{r.city_name}</span>}
                        </td>
                        <td className="p-3 text-[11px] text-slate-500">
                          {r.reject_reason || 'Survives to supply gate'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: IMPORT BACKLOG QUEUE */}
      {activeTab === 'import_queue' && (
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Discovery Import Backlog Queue</h3>
              <p className="text-xs text-slate-500 mt-0.5">Categories with &lt; 8 listings in DB are queued here for automated discovery via Overpass & Google Places.</p>
            </div>
            <span className="bg-amber-100 text-amber-800 font-bold text-xs px-3 py-1 rounded-xl">
              {importJobs.length} Jobs Queued
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-700 font-bold uppercase text-[10px] tracking-wider">
                  <th className="p-3">ID</th>
                  <th className="p-3">Priority</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Attempts</th>
                  <th className="p-3">Found / Imported</th>
                  <th className="p-3">Last Error / Warnings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                {importJobs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400 font-semibold">
                      No import jobs pending in queue. Upload a Semrush CSV to populate the discovery queue.
                    </td>
                  </tr>
                ) : (
                  importJobs.map((j) => (
                    <tr key={j.id} className="hover:bg-slate-50 transition">
                      <td className="p-3 font-mono">#{j.id}</td>
                      <td className="p-3 font-bold text-emerald-700">{j.priority}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded font-bold text-[10px] uppercase ${
                          j.status === 'done' ? 'bg-emerald-100 text-emerald-800' :
                          j.status === 'blocked' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {j.status}
                        </span>
                      </td>
                      <td className="p-3 font-mono">{j.attempts}</td>
                      <td className="p-3 font-mono">{j.found_count} / {j.imported_count}</td>
                      <td className="p-3 text-[11px] text-slate-500">{j.last_error || 'Clean execution'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: HUMAN REVIEW & FACTS AUDIT */}
      {activeTab === 'content_queue' && (
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Human Review & Facts Snapshot Audit</h3>
              <p className="text-xs text-slate-500 mt-0.5">Generated pages must pass numeric audit & business name audit against `facts_snapshot` before publication.</p>
            </div>
            <span className="bg-emerald-100 text-emerald-800 font-bold text-xs px-3 py-1 rounded-xl">
              {contentJobs.length} Pages Pending
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-700 font-bold uppercase text-[10px] tracking-wider">
                  <th className="p-3">ID</th>
                  <th className="p-3">Page Type</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Facts Snapshot</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                {contentJobs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-400 font-semibold">
                      No content jobs pending review.
                    </td>
                  </tr>
                ) : (
                  contentJobs.map((cj) => (
                    <tr key={cj.id} className="hover:bg-slate-50 transition">
                      <td className="p-3 font-mono">#{cj.id}</td>
                      <td className="p-3 font-bold uppercase">{cj.page_type}</td>
                      <td className="p-3">
                        <span className="bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded text-[10px] uppercase">
                          {cj.status}
                        </span>
                      </td>
                      <td className="p-3 text-[11px] font-mono text-slate-600">
                        {JSON.stringify(cj.facts_snapshot || {}).slice(0, 60)}...
                      </td>
                      <td className="p-3 text-right">
                        <button className="bg-emerald-700 text-white font-bold text-[11px] px-3 py-1 rounded-lg hover:bg-emerald-800 transition mr-2">
                          Approve Page
                        </button>
                        <button className="bg-slate-200 text-slate-700 font-bold text-[11px] px-3 py-1 rounded-lg hover:bg-slate-300 transition">
                          View Snapshot
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: QUOTE FLYWHEEL DATA */}
      {activeTab === 'quotes' && (
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Quote Flywheel — Real User Submissions</h3>
              <p className="text-xs text-slate-500 mt-0.5">Captures actual pricing & lead inquiries submitted by visitors across Zha Search listing pages.</p>
            </div>
            <span className="bg-emerald-100 text-emerald-800 font-bold text-xs px-3 py-1 rounded-xl">
              {quoteRequests.length} Quotes Received
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-700 font-bold uppercase text-[10px] tracking-wider">
                  <th className="p-3">Date</th>
                  <th className="p-3">User Name</th>
                  <th className="p-3">Phone / Contact</th>
                  <th className="p-3">Category / City</th>
                  <th className="p-3">Query Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                {quoteRequests.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-400 font-semibold">
                      No user quotes received yet. Quotes submitted via listing cards & detail modals will appear here.
                    </td>
                  </tr>
                ) : (
                  quoteRequests.map((q) => (
                    <tr key={q.id} className="hover:bg-slate-50 transition">
                      <td className="p-3 text-slate-400 font-mono">
                        {new Date(q.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </td>
                      <td className="p-3 font-bold text-slate-900">{q.user_name}</td>
                      <td className="p-3 font-mono text-emerald-700">{q.phone}</td>
                      <td className="p-3">
                        <span className="bg-slate-100 font-bold px-2 py-0.5 rounded text-[10px]">{q.category_name || 'Services'} - {q.city_name || 'Chennai'}</span>
                      </td>
                      <td className="p-3 text-[11px] text-slate-600 leading-relaxed">{q.message}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
